import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { backupAPI } from '../services/api';

function GaugeCard({ repo, used, capacity, free, percent }) {
  const pct   = Math.min(Math.max(percent || 0), 100);
  const color = pct < 75 ? '#16a34a' : pct < 90 ? '#ca8a04' : '#dc2626';
  const radius = 60;
  const stroke = 14;
  const circ   = Math.PI * radius;
  const dash   = (pct / 100) * circ;

  return (
    <div style={s.gaugeCard}>
      <div style={s.gaugeName}>{repo}</div>
      <svg width={160} height={100} viewBox="0 0 160 100">
        <path d="M 20 90 A 60 60 0 0 1 140 90" fill="none" stroke="#e2e8f0" strokeWidth={stroke} strokeLinecap="round" />
        <path
          d="M 20 90 A 60 60 0 0 1 140 90"
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
        />
        <text x="80" y="80" textAnchor="middle" fontSize="22" fontWeight="800" fill={color}>
          {pct.toFixed(1)}%
        </text>
      </svg>
      <div style={s.gaugeStats}>
        <span>Totale: {capacity?.toFixed(0)} GB</span>
        <span>Libero: {free?.toFixed(0)} GB</span>
        <span>Usato: {(capacity - free)?.toFixed(0)} GB</span>
      </div>
    </div>
  );
}

export default function StoragePage({ customer }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    backupAPI.getStorage(customer)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [customer]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Caricamento...</div>;
  if (!data)   return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Errore caricamento dati</div>;

  // Raggruppa per tipo
  const local = (data.latest || []).filter(r => r.repository_type === 'Local' || (!r.is_object_storage && r.repository_type !== 'SOBR' && r.repository_type !== 'ObjectStorage'));
  const s3    = (data.latest || []).filter(r => r.is_object_storage || r.repository_type === 'ObjectStorage');
  const sobr  = (data.latest || []).filter(r => r.repository_type === 'SOBR');

  // Trend: aggrega per repository
  const repoNames = [...new Set((data.trend || []).map(t => t.repository))];
  const trendMap  = {};
  (data.trend || []).forEach(t => {
    const key = t.hour;
    if (!trendMap[key]) trendMap[key] = { hour: key };
    trendMap[key][t.repository] = parseFloat(t.used_percent?.toFixed(1) || 0);
  });
  const trendData = Object.values(trendMap).sort((a, b) => new Date(a.hour) - new Date(b.hour));

  const COLORS = ['#2563eb','#16a34a','#ca8a04','#dc2626','#7c3aed','#0891b2'];

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700, color: '#1e293b' }}>Storage</h2>

      {local.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>💾 Repository Usage (Locale)</div>
          <div style={s.gaugeRow}>
            {local.map((r, i) => (
              <GaugeCard key={i} repo={r.repository}
                used={r.used_gb} capacity={r.capacity_gb}
                free={r.free_gb} percent={r.used_percent} />
            ))}
          </div>
        </div>
      )}

      {s3.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>☁️ Repository Usage S3</div>
          <div style={s.gaugeRow}>
            {s3.map((r, i) => (
              <GaugeCard key={i} repo={r.repository}
                used={r.used_gb} capacity={r.capacity_gb}
                free={r.free_gb} percent={r.used_percent} />
            ))}
          </div>
        </div>
      )}

      {sobr.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>🗄️ Scale-Out Backup Repository (SOBR)</div>
          <div style={s.gaugeRow}>
            {sobr.map((r, i) => (
              <GaugeCard key={i} repo={r.repository}
                used={r.used_gb} capacity={r.capacity_gb}
                free={r.free_gb} percent={r.used_percent} />
            ))}
          </div>
        </div>
      )}

      {trendData.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>📈 Repository Capacity Trend (7 giorni)</div>
          <div style={{ padding: '8px 16px 20px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendData}>
                <XAxis dataKey="hour" tick={{ fontSize: 10 }}
                  tickFormatter={v => new Date(v).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} />
                <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n) => [`${v}%`, n]} />
                <Legend />
                {repoNames.map((repo, i) => (
                  <Line key={repo} type="monotone" dataKey={repo}
                    stroke={COLORS[i % COLORS.length]} dot={false} strokeWidth={2} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  card:      { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 20 },
  cardTitle: { padding: '14px 20px', fontWeight: 700, fontSize: 15, color: '#1e293b', borderBottom: '1px solid #f1f5f9' },
  gaugeRow:  { display: 'flex', flexWrap: 'wrap', gap: 20, padding: 20 },
  gaugeCard: { textAlign: 'center', background: '#f8fafc', borderRadius: 12, padding: '16px 20px', minWidth: 180 },
  gaugeName: { fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 8 },
  gaugeStats:{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#64748b', marginTop: 4 },
};
